import type { GpuCloudPool, GpuDesktop, GpuModel, GpuModelSplit } from './gpu-types';

/** The glossary's names: never a model, library or vendor name. */
export const modelName: Record<GpuModel, string> = {
  understand: 'Understanding',
  read: 'Page reader',
  reason: 'Reasoner',
};

/** The colour family each model shares with the cards strip (page reader, understanding, reasoner). */
export const modelTone: Record<GpuModel, string> = {
  understand: 'understanding',
  read: 'ocr',
  reason: 'reasoner',
};

const ORDER: GpuModel[] = ['understand', 'read', 'reason'];

/** The card's short name: "RTX 4090" from "NVIDIA GeForce RTX 4090". */
export function cardName(card: string): string {
  return card.replace(/^NVIDIA\s+/i, '').replace(/^GeForce\s+/i, '');
}

/** A desktop's one word: what it is doing now. */
export function desktopWord(d: GpuDesktop): string {
  if (d.state === 'paused') return 'Paused at the desk';
  if (d.state === 'draining') return 'Draining';
  if (d.models.length === 0) return 'Nothing placed';
  if (d.models.some((m) => m.state === 'loading')) return 'Loading';
  return d.models.some((m) => m.inFlight > 0) ? 'Working' : 'Ready';
}

/** A cloud pool's one word. */
export function cloudWord(p: GpuCloudPool): string {
  if (p.state === 'off') return 'Off';
  if (p.state === 'up') return p.wanted === 1 ? 'Running' : `Running, ${p.wanted} cards`;
  return p.ready === 0 ? 'Starting' : `${p.ready} of ${p.wanted} ready`;
}

/** The desk share of a model's calls in the window, 0..1; null when it had none. */
export function deskShare(m: GpuModelSplit): number | null {
  const total = m.desk + m.cloud + m.unserved;
  return total === 0 ? null : m.desk / total;
}

/** Every model in the screen's order, with an empty row for one the relay did not name. */
export function splitsOf(models: GpuModelSplit[]): GpuModelSplit[] {
  return ORDER.map(
    (model) =>
      models.find((m) => m.model === model) ?? {
        model,
        desktopCopies: 0,
        inFlight: 0,
        waiting: 0,
        desk: 0,
        cloud: 0,
        unserved: 0,
      },
  );
}

/** GiB with one decimal from MiB. */
export function gib(mib: number): string {
  return (mib / 1024).toFixed(1);
}
